import styled, { css, useTheme } from "styled-components";
import { space, SpaceProps } from "styled-system";
import { compose } from "lodash/fp";
import { desaturate, getLuminance, shade, tint } from "polished";
import { ShownRange } from "./types";
import { Fragment } from "react";
import { useSettings } from "Settings";
import { formatTime, ROW_MINS } from "./utils";

const dim = compose(tint(0.3), desaturate(0.1));

type ContainerProps = {
  active: boolean;
  height: number;
  top: number;
  colorIndex: number;
};

const Container = styled.div.attrs((props: ContainerProps) => ({
  style: {
    height: props.height - 2,
    top: props.top,
  },
}))<ContainerProps>`
  position: absolute;
  width: calc(100% - 2px);
  background-color: ${(props) => props.theme.blockColors[props.colorIndex]};
  left: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border: 2px solid ${(props) => props.theme.blockColors[props.colorIndex]};

  ${(props) => {
    if (!props.active) {
      return "";
    }

    const c1 = props.theme.blockColors[props.colorIndex];
    const c2 = dim(c1);
    const c = shade(0.3, props.theme.blockColors[props.colorIndex]);

    return css`
      border-color: ${c};
      background-image: linear-gradient(
        45deg,
        ${c1} 25%,
        ${c2} 25%,
        ${c2} 50%,
        ${c1} 50%,
        ${c1} 75%,
        ${c2} 75%,
        ${c2} 100%
      );
      background-size: 56.57px 56.57px;
    `;
  }};
`;

const Description = styled.p`
  font-size: 12px;
  font-weight: 600;
  padding-bottom: 3px;
  margin-bottom: -3px;
  color: white;
  text-align: center;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.6);
  line-height: 1;
  max-width: 90%;
  overflow: hidden;
  text-overflow: elipsis;
  flex: 0 0 auto;
`;

const Time = styled.p<SpaceProps>`
  font-size: 12px;
  color: white;
  text-align: center;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.3);
  ${space};
`;

const Tag = styled.span`
  background: ${(props) => props.theme.colors.background};
  border-radius: 4px;
  font-size: 12px;
  padding: 0px;
  padding-left: 6px;
  padding-right: 6px;
  color: ${(props) => props.theme.colors.grey4};
  display: inline-block;
  line-height: 20px;
`;

const TagContainer = styled.div<{ rows: number }>`
  text-align: center;
  max-height: ${(props) => props.rows * 26}px;
  overflow: hidden;
`;

export type CalendarItemProps = {
  id: string;
  description: string;
  start: number;
  length: number;
  colorIndex: number;
  rowHeight: number;
  active: boolean;
  tags: string[];
  shownRange: ShownRange;
  headerOffset: number;
};

const CalendarItem = ({
  description,
  start,
  length,
  colorIndex,
  rowHeight,
  active,
  tags,
  shownRange,
  headerOffset,
}: CalendarItemProps) => {
  const [settings] = useSettings();
  const theme = useTheme();
  const pxPerMin = rowHeight / ROW_MINS;
  const height = length * pxPerMin;

  const showTime =
    (length <= 30 && description === "") ||
    (height > 50 && description === "") ||
    height > 35;
  const showTags = tags.length > 0 && height > 30;
  const fontSize = height > 60 ? 14 : 12;
  const tagCount = height < 50 ? 0 : 9999999;

  const lum = getLuminance(theme.blockColors[colorIndex]);
  const readable = lum > 0.65 ? "#666" : "white";
  const shadowColor = lum > 0.65 ? "rgba(255,255,255,0.0)" : "rgba(0,0,0,0.6)";
  const timeText = `${formatTime(start, settings.timeFormat)} - ${formatTime(
    start + length,
    settings.timeFormat
  )}`;

  return (
    <Container
      active={active}
      colorIndex={colorIndex}
      height={height}
      top={(start - shownRange[0]) * pxPerMin + headerOffset}
      title={timeText}
    >
      <Description
        style={{
          fontSize,
          color: readable,
          textShadow: `0px 1px 2px ${shadowColor} `,
        }}
      >
        {description}
      </Description>
      {showTime && (
        <Time
          mb={tags.length > 0 && height > 75 ? 1 : 0}
          style={{
            color: readable,
            textShadow: `0px 1px 2px ${shadowColor} `,
          }}
        >
          {timeText}
        </Time>
      )}
      {showTags && (
        <TagContainer rows={height < 80 ? 1 : 999}>
          {tags.slice(0, tagCount).map((x) => (
            <Fragment key={x}>
              <Tag>#{x}</Tag>{" "}
            </Fragment>
          ))}
        </TagContainer>
      )}
    </Container>
  );
};

export default CalendarItem;
